import { type JSX, type ReactNode } from "react";
import "./loading.css";
import Logo from "/logo.svg";
import { classNames } from "@via/utils/helpers.tsx";

/**
 * Overlay loading component
 * @param relative - If true, uses absolute positioning (for use inside a relative container).
 *                   If false (default), uses fixed positioning (full screen overlay).
 */
export const OverlayLoading = ({
  relative = false,
}: {
  relative?: boolean;
}) => {
  return (
    <div
      className={classNames(
        "flex items-center justify-center bg-white/75",
        relative
          ? "absolute inset-0 z-10 rounded-md"
          : "pointer-events-auto fixed inset-0 z-50"
      )}>
      <Spinner loading />
    </div>
  );
};

export const LogoSpinner = (): JSX.Element => {
  return (
    <button
      disabled
      type="button"
      className="me-2 inline-flex items-center rounded-lg bg-white px-5 py-2.5 text-sm font-medium text-gray-900 focus:z-10">
      <img
        className="loader mx-auto h-10 w-auto"
        src={Logo}
        title="Loading..."
        alt="Foundry Connect logo"
      />
    </button>
  );
};

export const Spinner = ({
  spinColor = "#fff",
  loading = false,
  children,
  className,
}: {
  spinColor?: string;
  loading?: boolean;
  children?: ReactNode;
  className?: string;
}): JSX.Element => {
  return (
    <div className="flex h-full w-full items-center justify-center">
      {loading && (
        <svg
          aria-hidden="true"
          role="status"
          className={classNames(
            "animate-spin",
            className ?? "mx-0.5 h-5 w-4 text-gray-900 dark:text-gray-600"
          )}
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill={spinColor}
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentColor"
          />
        </svg>
      )}
      {children}
    </div>
  );
};

/** @params count (number): Specifies the number of skeleton loader boxes to render. **/
export const SkeletonLoader = ({ count }: { count: number }): JSX.Element => {
  const skeleton: number[] = [...Array(count).keys()].map(i => i + 1);
  return (
    <>
      {skeleton.map(item => (
        <div
          key={item}
          title="Content is loading"
          className="mx-auto w-full rounded-md border p-4 shadow">
          <div className="flex animate-pulse space-x-4">
            <div className="h-10 w-10 rounded-full bg-slate-200"></div>
            <div className="flex-1 space-y-6 py-1">
              <div className="h-2 rounded bg-slate-200"></div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 h-2 rounded bg-slate-200"></div>
                  <div className="col-span-1 h-2 rounded bg-slate-200"></div>
                </div>
                <div className="h-2 rounded bg-slate-200"></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export const TitleLoader = (): JSX.Element => {
  return (
    <div className="my-3.5 h-4 w-full max-w-72 rounded bg-slate-200 md:pb-2 md:pt-2" />
  );
};
